import { Modal } from "components";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import CountryDropdown from "components/dropdowns/reactselect/CountryDropdown";
import StateDropdown from "components/dropdowns/reactselect/StateDropdown";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import UrlInput from "components/urlinput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React from "react";
import { findCountryById, findStateById } from "utilities/countrystateutils";
import CompanyNameField from "./CompanyNameField";
import { useCustomQuery } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";
import { Message } from "semantic-ui-react";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import { getCountryFormDefinition } from "CMS/utils";

interface Props {
  onCreated: (company: server.dto.Company) => void;
  onClose: () => void;

  defaults?: Partial<NewCompanyFormState>

  disableArtist?: boolean;
  disableVenue?: boolean;
  disablePromoter?: boolean;
}

interface NewCompanyFormState {
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  country: server.dto.Country;
  state: server.dto.State;
  euVatNumber: string;
  fiscalNumber1: string;
  phoneNumber: string;
  website: string;
  isArtist: boolean;
  isVenue: boolean;
  isPromoter: boolean;
  capacity: number;
  googlePlaceId?: string;
  latLng: server.dto.Geo;
}

const CompanyNewModal = (props: Props) => {
  const initialCountry = serverReference.countries.find(c => c.key === site.userPreference.regional.country);
  const [formState, setFormState] = React.useState<NewCompanyFormState>({
    companyName: "",
    addressLine1: "",
    addressLine2: "",
    postalCode: "",
    city: "",
    country: initialCountry,
    state: initialCountry.states.find(s => s.isCountryDefault),
    euVatNumber: "",
    fiscalNumber1: "",
    phoneNumber: "",
    website: "",
    isArtist: false,
    isVenue: false,
    isPromoter: false,
    capacity: undefined,
    latLng: undefined,
    ...props.defaults
  });

  const onChange = (newData: Partial<NewCompanyFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const onGooglePlaceSelected = (place: server.dto.GooglePlace) => {
    onChange({
      companyName: place.name ?? "",
      addressLine1: place.address.addressLine1 ?? "",
      addressLine2: place.address.addressLine2 ?? "",
      postalCode: place.address.postalCode ?? "",
      city: place.address.city ?? "",
      country: findCountryById(place.address.country) ?? undefined,
      state: findStateById(place.address.stateId) ?? undefined,
      website: place.website ?? "",
      googlePlaceId: place.placeId,
      latLng: place.latLng
    });
  }

  const onNorwegianCompanySelected = (data: server.dto.NorwegianCompanyRegistration) => {
    onChange({
      companyName: data.name ?? "",
      addressLine1: data.address?.addressLine1 ?? "",
      addressLine2: data.address?.addressLine2 ?? "",
      postalCode: data.address?.postalCode ?? "",
      city: data.address?.city ?? "",
      country: data.address ? findCountryById(data.address.country) : findCountryById("norway"),
      state: data.address ? findStateById(data.address.stateId) : findCountryById("norway").states.find(s => s.isCountryDefault),
      website: data.website ?? "",
    })
  }

  const submit = () => {
    return window.CoreApi.Company.Create({
      googlePlaceId: formState.googlePlaceId,
      isArtist: formState.isArtist ? "active" : "no",
      isPromoter: formState.isPromoter ? "active" : "no",
      isVenue: formState.isVenue ? "active" : "no",
      regularAddress: {
        addressLine1: formState.addressLine1,
        addressLine2: formState.addressLine2,
        postalCode: formState.postalCode,
        city: formState.city,
        stateId: formState.state?.key
      },
      regularName: formState.companyName,
      fiscalNumber1: formDefinition.fiscalNumber1.display ? formState.fiscalNumber1 : undefined,
      euVatNumber: formDefinition.euVatNumber.display ? formState.euVatNumber : undefined,
      phoneNumbers: convertToPhoneNumbers(formState.phoneNumber),
      website: formState.website,
      venueCapacity: formState.isVenue ? formState.capacity : undefined,
      geoLocation: formState.latLng
    });
  };

  const onSuccess = async (companyId: number) => {
    const res = await window.CoreApi.Company.GetSidePanelData(companyId).then(r => r.data);
    props.onCreated(res.data.company);
  }

  const debouncedCompanyName = useDebounce(formState.companyName, 500);
  const companyExistsQuery = useCustomQuery({
    queryKey: ["companyExists", debouncedCompanyName],
    queryFn: () => window.CoreApi.Company.NameExists({ name: debouncedCompanyName }).then(r => r.data),
    enabled: !!debouncedCompanyName,
  });

  const fg = site.currentEnvironment.functionGroups;
  const formDefinition = getCountryFormDefinition(formState.country.key);

  return (
    <Modal
      isOpen
      title={site.currentEnvironment.company.newLabel}
      handleClose={props.onClose}
      width="small"
      hasAutoScrollBox
      minHeight={400}
    >
      <div className="grow1 scrollbox-auto" >
        <EditSectionTemplate
          submit={submit}
          noPanels
          onSuccess={onSuccess}
          sections={[
            {
              key: "companyDetails",
              type: "regular",
              title: site.currentEnvironment.company.newLabel,
              fields: [
                {
                  key: "name",
                  label: RESX.GeneralLabels.Name,
                  renderEditNew: () => (
                    <CompanyNameField
                      country={formState.country}
                      name={formState.companyName}
                      onChange={(newName) => onChange({ companyName: newName })}
                      onGooglePlaceSelected={onGooglePlaceSelected}
                      onNorwegianCompanySelected={onNorwegianCompanySelected}
                    />
                  )
                }, {
                  key: "addressLine1",
                  label: RESX.Address.AddressLine + " 1",
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.address.maxAddressLineLength}
                      value={formState.addressLine1}
                      onChange={(value) => onChange({ addressLine1: value })}
                    />
                  )
                },
                {
                  key: "addressLine2",
                  label: RESX.Address.AddressLine + " 2",
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.address.maxAddressLineLength}
                      value={formState.addressLine2}
                      onChange={(value) => onChange({ addressLine2: value })}
                    />
                  )
                }, {
                  key: "postalCode",
                  label: RESX.Address.PostalCode,
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.address.maxPostalCodeLength}
                      value={formState.postalCode}
                      onChange={(value) => onChange({ postalCode: value })}
                    />
                  )
                }, {
                  key: "city",
                  label: RESX.Address.City,
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.address.maxCityLength}
                      value={formState.city}
                      onChange={(value) => onChange({ city: value })}
                    />
                  )
                }, {
                  key: "country",
                  label: RESX.Address.Country,
                  renderEditNew: () => (
                    <CountryDropdown
                      value={formState.country}
                      onChange={(value) => onChange({ country: value, state: value.states.find(s => s.isCountryDefault) })}
                    />
                  )
                }, {
                  key: "state",
                  label: RESX.Address.State,
                  renderEditNew: () => (
                    <StateDropdown
                      value={formState.state}
                      options={formState.country?.states ?? []}
                      onChange={(value) => onChange({ state: value })}
                    />
                  )
                }, {
                  key: "euVatNumber",
                  label: formDefinition.euVatNumber.label,
                  sublabel: formDefinition.euVatNumber.sublabel,
                  hidden: !formDefinition.euVatNumber.display,
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.company.maxEuVatNumberLength}
                      value={formState.euVatNumber}
                      onChange={(value) => onChange({ euVatNumber: value })}
                    />
                  )
                }, {
                  key: "fiscalNumber1",
                  label: formDefinition.fiscalNumber1.label,
                  sublabel: formDefinition.fiscalNumber1.sublabel,
                  hidden: !formDefinition.fiscalNumber1.display,
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.company.maxFiscalNumber1Length}
                      value={formState.fiscalNumber1}
                      onChange={(value) => onChange({ fiscalNumber1: value })}
                    />
                  )
                }, {
                  key: "phoneNumber",
                  label: RESX.Company.Phone,
                  renderEditNew: () => (
                    <PhoneInput
                      value={formState.phoneNumber}
                      onChange={(value) => onChange({ phoneNumber: value })}
                    />
                  )
                }, {
                  key: "website",
                  label: RESX.GeneralLabels.Website,
                  renderEditNew: () => (
                    <UrlInput
                      validatorKey="website"
                      value={formState.website}
                      onValueChange={(value) => onChange({ website: value })}
                      maxLength={serverReference.constants.url.maxUrlLength}
                    />
                  )
                },
                {
                  key: "profile",
                  label: RESX.Company.Profile,
                  renderEditNew: () => (
                    <div className="flex-column gap">
                      <div>
                        {fg.artist.enabled && <Checkbox disabled={props.disableArtist} label={fg.artist.singular} checked={formState.isArtist} onChange={(_, checked) => onChange({ isArtist: checked })} />}
                      </div>
                      <div>
                        {fg.venue.enabled && <Checkbox disabled={props.disableVenue} label={fg.venue.singular} checked={formState.isVenue} onChange={(_, checked) => onChange({ isVenue: checked })} />}
                      </div>
                      <div>
                        {fg.promoter.enabled && <Checkbox disabled={props.disablePromoter} label={fg.promoter.singular} checked={formState.isPromoter} onChange={(_, checked) => onChange({ isPromoter: checked })} />}
                      </div>
                    </div>
                  )
                }, {
                  key: "capacity",
                  label: RESX.Venue.Capacity,
                  hidden: !formState.isVenue,
                  renderEditNew: () => (
                    <OptionalNumberInput
                      value={formState.capacity}
                      maxValue={serverReference.constants.venue.maxCapacity}
                      labelContent="PAX"
                      onChange={(value) => onChange({ capacity: value })}
                      onEnterPressed={value => onChange({ capacity: value })}
                      decimalPoints={0}
                      canBeEmpty
                      canBeNegative={false}
                      width={100}
                    />
                  )
                }
              ],
            },
            {
              key: "companyExistsWarning",
              type: "custom",
              hidden: !companyExistsQuery.data?.data,
              render: () => <Message color="red" className="margin-top margin-bottom">
                {RESX.Company.ACompanyWithThisNameAlreadyExists}
              </Message>
            }
          ]}
        />
      </div>
    </Modal >
  );
}

export default CompanyNewModal;